import { useQuery } from '@tanstack/react-query';

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AddCircle, Article, Clear, Delete, Download, DriveFileMove, Edit, Refresh, Search, UnfoldLess, UnfoldMore } from '@mui/icons-material';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Skeleton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import { useApiClient, usePersistedSetting, useToastService } from '@@common';
import { ConfirmDialog, GalleryHeader, GalleryStatistics } from '@@components';

import { getValidationErrorMessage, parseValidationError } from '../../api/ApiValidationUtils';
import { ContextClient, ContextCodeBaseTypeModel, ContextCodeClient, ContextModel, SubDomainContextsModel, SubDomainModel } from '../../api/api';
import { useDesignerScreenContext } from '../../components/Chat/DesignerScreenContext';
import PromptChatPanel from '../../components/Chat/PromptChatPanel';
import CreateContextDialog from './Components/CreateContextDialog';
import CreateSubDomainDialog from './Components/CreateSubDomainDialog';
import SubDomainSection from './Components/SubDomainSection';
import { contextDesignerQueryKeys } from './ContextDesignerQueryKeys';
import { useServicesChatAdapter } from './Hooks/useServicesChatAdapter';
import { summarizeContextArtifacts } from './Utils/contextArtifactCounts';

interface ServiceListViewProps {
	initialSubDomainId?: string;
}

type DeleteTarget = { type: 'subDomain'; value: SubDomainModel } | { type: 'context'; value: ContextModel };
type DescriptionTarget = { type: 'subDomain'; value: SubDomainModel } | { type: 'context'; value: ContextModel };

const EXPANDED_SUB_DOMAIN_IDS_STORAGE_KEY = 'subDomainList.expandedSubDomainIds';
const PAGE_TITLE = 'Services';
const SUB_DOMAIN_ENTITY_LABEL = 'SubDomain';
const SUB_DOMAIN_COLLECTION_LABEL = 'SubDomains';
const normalizeSearch = (value: string | undefined) => value?.trim().toLocaleLowerCase() ?? '';

const includesSearch = (value: string | undefined, searchValue: string) => normalizeSearch(value).includes(searchValue);

const addExpandedSubDomainIds = (current: string[], idsToAdd: string[]) => Array.from(new Set([...current, ...idsToAdd]));

const removeExpandedSubDomainIds = (current: string[], idsToRemove: string[]) => {
	const idsToRemoveSet = new Set(idsToRemove);
	return current.filter((id) => !idsToRemoveSet.has(id));
};

const toggleExpandedSubDomainId = (current: string[], subDomainId: string) => {
	if (current.includes(subDomainId)) {
		return current.filter((id) => id !== subDomainId);
	}

	return [...current, subDomainId];
};

export default function ServiceListView({ initialSubDomainId }: ServiceListViewProps) {
	useDesignerScreenContext({ ScreenArea: 'Services' });
	const client = useApiClient(ContextClient);
	const contextCodeClient = useApiClient(ContextCodeClient);
	const navigate = useNavigate();
	const toastService = useToastService();
	const servicesChatAdapter = useServicesChatAdapter();

	const [filter, setFilter] = useState('');
	const [expandedSubDomainIds, setExpandedSubDomainIds] = usePersistedSetting<string[]>(
		EXPANDED_SUB_DOMAIN_IDS_STORAGE_KEY,
		initialSubDomainId ? [initialSubDomainId] : [],
	);
	const [openCreateSubDomainDialog, setOpenCreateSubDomainDialog] = useState(false);
	const [createContextSubDomain, setCreateContextSubDomain] = useState<SubDomainModel | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [descriptionTarget, setDescriptionTarget] = useState<DescriptionTarget | null>(null);
	const [descriptionValue, setDescriptionValue] = useState('');
	const [isSavingDescription, setIsSavingDescription] = useState(false);
	const [subDomainActionMenuAnchor, setSubDomainActionMenuAnchor] = useState<HTMLElement | null>(null);
	const [selectedSubDomainForMenu, setSelectedSubDomainForMenu] = useState<SubDomainModel | null>(null);
	const [contextActionMenuAnchor, setContextActionMenuAnchor] = useState<HTMLElement | null>(null);
	const [selectedContextForMenu, setSelectedContextForMenu] = useState<ContextModel | null>(null);
	const [moveContextTarget, setMoveContextTarget] = useState<ContextModel | null>(null);
	const [moveDestinationSubDomainId, setMoveDestinationSubDomainId] = useState('');
	const [isMovingContext, setIsMovingContext] = useState(false);

	const {
		data: subDomainsData,
		isLoading,
		isFetching,
		refetch,
	} = useQuery<SubDomainContextsModel[]>({
		queryKey: contextDesignerQueryKeys.subDomains(),
		queryFn: async () => client.getSubDomains(),
	});

	const allContexts = useMemo(() => (subDomainsData ?? []).flatMap((item) => item.contexts ?? []), [subDomainsData]);
	const handleRefresh = useCallback(async () => {
		await refetch();
	}, [refetch]);
	React.useEffect(() => {
		if (!initialSubDomainId) {
			return;
		}

		setExpandedSubDomainIds((current) => addExpandedSubDomainIds(current, [initialSubDomainId]));
	}, [initialSubDomainId, setExpandedSubDomainIds]);

	const searchValue = normalizeSearch(filter);
	const filteredSubDomains = useMemo(() => {
		const subDomains = subDomainsData ?? [];
		if (!searchValue) {
			return subDomains;
		}

		return subDomains
			.map((item) => {
				const subDomainMatches = includesSearch(item.subDomain?.name, searchValue) || includesSearch(item.subDomain?.description, searchValue);
				const matchingContexts =
					item.contexts?.filter((context) => includesSearch(context.contextName, searchValue) || includesSearch(context.description, searchValue)) ?? [];

				return {
					...item,
					contexts: subDomainMatches ? item.contexts : matchingContexts,
				};
			})
			.filter(
				(item) => item.contexts.length > 0 || includesSearch(item.subDomain?.name, searchValue) || includesSearch(item.subDomain?.description, searchValue),
			);
	}, [searchValue, subDomainsData]);

	React.useEffect(() => {
		if (!searchValue) {
			return;
		}

		setExpandedSubDomainIds((current) =>
			addExpandedSubDomainIds(
				current,
				filteredSubDomains.map((item) => item.subDomain.id),
			),
		);
	}, [filteredSubDomains, searchValue, setExpandedSubDomainIds]);

	const contextCount = allContexts.length;
	const contextArtifactSummary = useMemo(() => summarizeContextArtifacts(subDomainsData ?? []), [subDomainsData]);
	const { entities: entityCount, operations: operationCount, byContextId: contextArtifactCountsById } = contextArtifactSummary;
	const expandedSubDomainIdSet = useMemo(() => new Set(expandedSubDomainIds), [expandedSubDomainIds]);

	const handleToggleSubDomain = useCallback(
		(subDomainId: string) => {
			setExpandedSubDomainIds((current) => toggleExpandedSubDomainId(current, subDomainId));
		},
		[setExpandedSubDomainIds],
	);

	const handleOpenContext = useCallback(
		(context: ContextModel) => {
			navigate(`/service/context/${context.id}`);
		},
		[navigate],
	);

	const handleOpenContextResources = useCallback(
		(context: ContextModel) => {
			navigate(`/service/${context.subDomainId}/context/${context.id}/resource`);
		},
		[navigate],
	);

	const handleOpenSubDomainResources = useCallback(
		(subDomain: SubDomainModel) => {
			navigate(`/service/${subDomain.id}/resource`);
		},
		[navigate],
	);

	const handleExpandAll = () => {
		setExpandedSubDomainIds((current) =>
			addExpandedSubDomainIds(
				current,
				filteredSubDomains.map((item) => item.subDomain.id),
			),
		);
	};

	const handleCollapseAll = () => {
		setExpandedSubDomainIds((current) =>
			removeExpandedSubDomainIds(
				current,
				filteredSubDomains.map((item) => item.subDomain.id),
			),
		);
	};

	const handleOpenSubDomainActionMenu = (event: React.MouseEvent<HTMLElement>, subDomain: SubDomainModel) => {
		event.stopPropagation();
		setSubDomainActionMenuAnchor(event.currentTarget);
		setSelectedSubDomainForMenu(subDomain);
	};

	const handleCloseSubDomainActionMenu = () => {
		setSubDomainActionMenuAnchor(null);
		setSelectedSubDomainForMenu(null);
	};

	const handleOpenContextActionMenu = (event: React.MouseEvent<HTMLElement>, context: ContextModel) => {
		event.stopPropagation();
		setContextActionMenuAnchor(event.currentTarget);
		setSelectedContextForMenu(context);
	};

	const handleCloseContextActionMenu = () => {
		setContextActionMenuAnchor(null);
		setSelectedContextForMenu(null);
	};

	const handleDownloadContextCode = async (codeBaseType: ContextCodeBaseTypeModel) => {
		if (!selectedContextForMenu) {
			return;
		}

		const context = selectedContextForMenu;
		handleCloseContextActionMenu();
		try {
			const zip = await contextCodeClient.downloadContextCode(context.id, codeBaseType);
			const downloadUrl = URL.createObjectURL(zip.data);
			const downloadLink = document.createElement('a');
			downloadLink.href = downloadUrl;
			downloadLink.download = `${context.contextName}-${codeBaseType.toLowerCase()}.zip`;
			document.body.appendChild(downloadLink);
			downloadLink.click();
			downloadLink.remove();
			URL.revokeObjectURL(downloadUrl);
			toastService.success(`${codeBaseType} code download started.`);
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to download ${codeBaseType.toLowerCase()} code: ${errorMessage}`);
		}
	};

	const handleDeleteContextFromMenu = () => {
		if (selectedContextForMenu) {
			setDeleteTarget({ type: 'context', value: selectedContextForMenu });
		}
		handleCloseContextActionMenu();
	};

	const handleMoveContextFromMenu = () => {
		if (selectedContextForMenu) {
			setMoveContextTarget(selectedContextForMenu);
			setMoveDestinationSubDomainId('');
		}
		handleCloseContextActionMenu();
	};

	const handleCloseMoveContextDialog = () => {
		if (isMovingContext) {
			return;
		}

		setMoveContextTarget(null);
		setMoveDestinationSubDomainId('');
	};

	const handleMoveContext = async () => {
		if (!moveContextTarget || !moveDestinationSubDomainId) {
			return;
		}

		const destination = subDomainsData?.find((item) => item.subDomain.id === moveDestinationSubDomainId)?.subDomain;
		setIsMovingContext(true);
		try {
			await client.moveContextToSubDomain(moveContextTarget.id, moveDestinationSubDomainId);
			setExpandedSubDomainIds((current) => addExpandedSubDomainIds(current, [moveDestinationSubDomainId]));
			await refetch();
			toastService.success(`Context "${moveContextTarget.contextName}" moved to "${destination?.name ?? 'the selected subdomain'}".`);
			setMoveContextTarget(null);
			setMoveDestinationSubDomainId('');
		} catch (error) {
			const validationError = parseValidationError(error);
			const errorMessage = validationError ? getValidationErrorMessage(validationError) : error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to move context: ${errorMessage}`);
		} finally {
			setIsMovingContext(false);
		}
	};

	const handleOpenDescriptionEditor = useCallback((target: DescriptionTarget) => {
		setDescriptionTarget(target);
		setDescriptionValue(target.type === 'subDomain' ? (target.value.description ?? '') : (target.value.description ?? ''));
	}, []);

	const handleEditSubDomainDescriptionFromMenu = () => {
		if (selectedSubDomainForMenu) {
			handleOpenDescriptionEditor({ type: 'subDomain', value: selectedSubDomainForMenu });
		}
		handleCloseSubDomainActionMenu();
	};

	const handleDeleteSubDomainFromMenu = () => {
		if (selectedSubDomainForMenu) {
			setDeleteTarget({ type: 'subDomain', value: selectedSubDomainForMenu });
		}
		handleCloseSubDomainActionMenu();
	};

	const handleCreateSubDomain = async (name: string, description: string) => {
		try {
			await client.createSubDomain({ subDomainName: name, subDomainDescription: description });
			toastService.success(`SubDomain "${name}" created successfully.`);
			refetch();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to create SubDomain: ${errorMessage}`);
		}
	};

	const handleCreateContext = async (name: string, description: string) => {
		if (!createContextSubDomain) {
			return;
		}

		try {
			await client.createContext(createContextSubDomain.id, { contextName: name, description, tags: [] });
			toastService.success(`Context "${name}" created successfully.`);
			setExpandedSubDomainIds((current) => addExpandedSubDomainIds(current, [createContextSubDomain.id]));
			refetch();
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to create context: ${errorMessage}`);
		}
	};

	const handleSaveDescription = async () => {
		if (!descriptionTarget) {
			return;
		}

		setIsSavingDescription(true);
		try {
			if (descriptionTarget.type === 'subDomain') {
				await client.updateSubDomainDescription(descriptionTarget.value.id, { description: descriptionValue });
				toastService.success(`SubDomain "${descriptionTarget.value.name}" description updated successfully.`);
			} else {
				await client.updateContextDescription(descriptionTarget.value.id, { description: descriptionValue });
				toastService.success(`Context "${descriptionTarget.value.contextName}" description updated successfully.`);
			}

			await refetch();
			setDescriptionTarget(null);
			setDescriptionValue('');
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to update description: ${errorMessage}`);
		} finally {
			setIsSavingDescription(false);
		}
	};

	const handleCloseDescriptionEditor = () => {
		if (isSavingDescription) {
			return;
		}

		setDescriptionTarget(null);
	};

	const handleConfirmDelete = async () => {
		if (!deleteTarget) {
			return;
		}

		setIsDeleting(true);
		try {
			if (deleteTarget.type === 'subDomain') {
				await client.deleteSubDomain(deleteTarget.value.id);
				toastService.success(`SubDomain "${deleteTarget.value.name}" deleted successfully.`);
			} else {
				await client.delete(deleteTarget.value.id);
				toastService.success(`Context "${deleteTarget.value.contextName}" deleted successfully.`);
			}
			await refetch();
		} catch (error) {
			const validationError = parseValidationError(error);
			const errorMessage = validationError ? getValidationErrorMessage(validationError) : error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Delete failed: ${errorMessage}`);
		} finally {
			setIsDeleting(false);
			setDeleteTarget(null);
		}
	};

	return (
		<Stack direction={{ xs: 'column', md: 'row' }} sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
			<Stack direction="column" spacing={2} sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'auto' }}>
				<GalleryHeader
					title={PAGE_TITLE}
					subtitle="Organize your domain into subdomains and bounded contexts."
					utilityActions={
						<>
							<Tooltip title="Manage service resources">
								<IconButton aria-label="Manage service resources" onClick={() => navigate('/resources')}>
									<Article />
								</IconButton>
							</Tooltip>
							<Tooltip title={`Refresh ${SUB_DOMAIN_COLLECTION_LABEL}`}>
								<IconButton aria-label={`Refresh ${SUB_DOMAIN_COLLECTION_LABEL}`} onClick={() => void handleRefresh()} disabled={isFetching}>
									{isFetching ? <CircularProgress size={20} /> : <Refresh />}
								</IconButton>
							</Tooltip>
						</>
					}
					primaryAction={
						<Button variant="contained" startIcon={<AddCircle />} onClick={() => setOpenCreateSubDomainDialog(true)}>
							Create Subdomain
						</Button>
					}
					statistics={
						<GalleryStatistics
							items={[
								{ label: 'SubDomains', value: subDomainsData?.length ?? 0 },
								{ label: 'Contexts', value: contextCount },
								{ label: 'Entities', value: entityCount },
								{ label: 'Operations', value: operationCount },
							]}
						/>
					}
					search={
						<TextField
							value={filter}
							onChange={(event) => setFilter(event.target.value)}
							placeholder={`Search ${SUB_DOMAIN_COLLECTION_LABEL} or bounded contexts`}
							size="small"
							fullWidth
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search fontSize="small" />
									</InputAdornment>
								),
								endAdornment: filter ? (
									<InputAdornment position="end">
										<IconButton aria-label="Clear search" size="small" onClick={() => setFilter('')}>
											<Clear fontSize="small" />
										</IconButton>
									</InputAdornment>
								) : undefined,
							}}
						/>
					}
					searchActions={
						<Stack direction="row" spacing={0.5}>
							<Tooltip title="Expand all">
								<span>
									<IconButton
										aria-label={`Expand all ${SUB_DOMAIN_COLLECTION_LABEL}`}
										size="small"
										onClick={handleExpandAll}
										disabled={filteredSubDomains.length === 0}
									>
										<UnfoldMore fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
							<Tooltip title="Collapse all">
								<span>
									<IconButton
										aria-label={`Collapse all ${SUB_DOMAIN_COLLECTION_LABEL}`}
										size="small"
										onClick={handleCollapseAll}
										disabled={filteredSubDomains.length === 0}
									>
										<UnfoldLess fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</Stack>
					}
				/>
				<Stack spacing={1.5} sx={{ minHeight: 0, flexGrow: 1, overflow: 'auto', pr: 0.5 }}>
					{isLoading ? (
						<>
							<Skeleton variant="rounded" height={92} />
							<Skeleton variant="rounded" height={92} />
							<Skeleton variant="rounded" height={92} />
						</>
					) : filteredSubDomains.length === 0 ? (
						<Box
							sx={{
								border: (theme) => `1px dashed ${theme.palette.divider}`,
								borderRadius: 2,
								py: 6,
								textAlign: 'center',
							}}
						>
							<Typography variant="subtitle1">No {SUB_DOMAIN_COLLECTION_LABEL} or contexts found</Typography>
							<Typography variant="body2" color="text.secondary">
								Adjust the search filter or create a new {SUB_DOMAIN_ENTITY_LABEL}.
							</Typography>
						</Box>
					) : (
						filteredSubDomains.map((item) => {
							const subDomain = item.subDomain;
							const contexts = item.contexts ?? [];
							const isExpanded = expandedSubDomainIdSet.has(subDomain.id);

							return (
								<SubDomainSection
									key={subDomain.id}
									contextArtifactCountsById={contextArtifactCountsById}
									contexts={contexts}
									isExpanded={isExpanded}
									onCreateContext={setCreateContextSubDomain}
									onEditContextDescription={(context) => handleOpenDescriptionEditor({ type: 'context', value: context })}
									onEditSubDomainDescription={(item) => handleOpenDescriptionEditor({ type: 'subDomain', value: item })}
									onOpenContext={handleOpenContext}
									onOpenContextResources={handleOpenContextResources}
									onOpenContextMenu={handleOpenContextActionMenu}
									onOpenSubDomainResources={handleOpenSubDomainResources}
									onOpenMenu={handleOpenSubDomainActionMenu}
									onToggle={handleToggleSubDomain}
									subDomain={subDomain}
								/>
							);
						})
					)}
				</Stack>

				<CreateSubDomainDialog open={openCreateSubDomainDialog} setOpen={setOpenCreateSubDomainDialog} onSuccess={handleCreateSubDomain} />
				<CreateContextDialog open={!!createContextSubDomain} setOpen={(open) => !open && setCreateContextSubDomain(null)} onSuccess={handleCreateContext} />
				<Menu
					anchorEl={subDomainActionMenuAnchor}
					open={!!subDomainActionMenuAnchor}
					onClose={handleCloseSubDomainActionMenu}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				>
					<MenuItem onClick={handleEditSubDomainDescriptionFromMenu}>
						<ListItemIcon>
							<Edit fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Edit description" />
					</MenuItem>
					<MenuItem onClick={handleDeleteSubDomainFromMenu}>
						<ListItemIcon>
							<Delete fontSize="small" color="error" />
						</ListItemIcon>
						<ListItemText primary={`Delete ${SUB_DOMAIN_ENTITY_LABEL}`} />
					</MenuItem>
				</Menu>
				<Menu
					anchorEl={contextActionMenuAnchor}
					open={!!contextActionMenuAnchor}
					onClose={handleCloseContextActionMenu}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				>
					<MenuItem
						onClick={handleMoveContextFromMenu}
						disabled={(subDomainsData?.filter((item) => item.subDomain.id !== selectedContextForMenu?.subDomainId).length ?? 0) === 0}
					>
						<ListItemIcon>
							<DriveFileMove fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Move to another subdomain" />
					</MenuItem>
					<MenuItem onClick={() => handleDownloadContextCode(ContextCodeBaseTypeModel.Domain)}>
						<ListItemIcon>
							<Download fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Download domain code (.zip)" />
					</MenuItem>
					<MenuItem onClick={() => handleDownloadContextCode(ContextCodeBaseTypeModel.Application)}>
						<ListItemIcon>
							<Download fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Download application code (.zip)" />
					</MenuItem>
					<MenuItem onClick={handleDeleteContextFromMenu}>
						<ListItemIcon>
							<Delete fontSize="small" color="error" />
						</ListItemIcon>
						<ListItemText primary="Delete context" />
					</MenuItem>
				</Menu>
				<Dialog open={!!moveContextTarget} onClose={handleCloseMoveContextDialog} maxWidth="xs" fullWidth>
					<DialogTitle>Move Context</DialogTitle>
					<DialogContent>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							Choose a new subdomain for &quot;{moveContextTarget?.contextName}&quot;.
						</Typography>
						<TextField
							select
							value={moveDestinationSubDomainId}
							onChange={(event) => setMoveDestinationSubDomainId(event.target.value)}
							label="Destination subdomain"
							disabled={isMovingContext}
							margin="dense"
							fullWidth
							autoFocus
						>
							{(subDomainsData ?? [])
								.filter((item) => item.subDomain.id !== moveContextTarget?.subDomainId)
								.map((item) => (
									<MenuItem key={item.subDomain.id} value={item.subDomain.id}>
										{item.subDomain.name}
									</MenuItem>
								))}
						</TextField>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseMoveContextDialog} disabled={isMovingContext}>
							Cancel
						</Button>
						<Button variant="contained" onClick={() => void handleMoveContext()} disabled={isMovingContext || !moveDestinationSubDomainId}>
							{isMovingContext ? 'Moving...' : 'Move'}
						</Button>
					</DialogActions>
				</Dialog>
				<Dialog open={!!descriptionTarget} onClose={handleCloseDescriptionEditor} maxWidth="sm" fullWidth>
					<DialogTitle>{descriptionTarget?.type === 'context' ? 'Edit Context Description' : `Edit ${SUB_DOMAIN_ENTITY_LABEL} Description`}</DialogTitle>
					<DialogContent>
						<TextField
							value={descriptionValue}
							onChange={(event) => setDescriptionValue(event.target.value)}
							label="Description"
							disabled={isSavingDescription}
							margin="dense"
							fullWidth
							multiline
							minRows={4}
							autoFocus
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseDescriptionEditor} disabled={isSavingDescription}>
							Cancel
						</Button>
						<Button variant="contained" onClick={handleSaveDescription} disabled={isSavingDescription}>
							{isSavingDescription ? 'Saving...' : 'Save'}
						</Button>
					</DialogActions>
				</Dialog>
				<ConfirmDialog
					open={!!deleteTarget}
					title={deleteTarget?.type === 'context' ? 'Delete Context' : `Delete ${SUB_DOMAIN_ENTITY_LABEL}`}
					message={
						deleteTarget?.type === 'context'
							? `Are you sure you want to delete "${deleteTarget.value.contextName}" together with all of its code bases? This action cannot be undone.`
							: deleteTarget?.type === 'subDomain'
								? `Are you sure you want to delete "${deleteTarget.value.name}"? This action cannot be undone and may affect any contexts that depend on it.`
								: ''
					}
					confirmText="Delete"
					confirmColor="error"
					confirmDisabled={isDeleting}
					cancelDisabled={isDeleting}
					onConfirm={handleConfirmDelete}
					onCancel={() => setDeleteTarget(null)}
				/>
			</Stack>
			<PromptChatPanel
				adapter={servicesChatAdapter}
				defaultTitle="Domain Modeller"
				area="Services"
				emptyStateText="Describe a business problem, and I will help break it into subdomains and bounded contexts."
				suggestedPrompts={[
					'Break down these requirements into subdomains',
					'What bounded contexts should we create?',
					'Describe this domain',
					'Help me plan the domain model for a new capability',
				]}
				storageKey="services-domain-modeler-chat.width"
				onComplete={handleRefresh}
				mobileCollapsible
			/>
		</Stack>
	);
}
